"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, Edit2, ExternalLink, Film, ImageIcon, LogOut, MessageSquare, MoreVerticalIcon, PenTool, Plus, Trash2, Users } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useDeleteImageHistory, useRenameImageHistory } from "@/hooks/usePlaygroundHistory";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { HistorySkeleton } from "./history-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import type { GalleryItem } from "@/lib/image-gen";
import type { Organization } from "@/lib/types";
interface ImageSidebarProps {
  galleryItems: GalleryItem[];
  isHistoryLoading?: boolean;
  onNewChat: () => void;
  onItemClick: (itemId: string) => void;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  currentItemId?: string | null;
  className?: string;
}
type ImageHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "item";
  key: string;
  item: GalleryItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_ITEM = 60;
interface ImageHistoryRowProps {
  rows: ImageHistoryRow[];
  currentItemId?: string | null;
  editingId: string | null;
  editPrompt: string;
  pendingFocusId: string | null;
  onItemClick: (itemId: string) => void;
  onEditPromptChange: (value: string) => void;
  onSaveEdit: (id: string, original: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (id: string) => void;
  onStartEdit: (id: string, prompt: string) => void;
  onEditFocused: () => void;
}
function getImageHistoryRowHeight(index: number, {
  rows
}: ImageHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_ITEM;
}
function EditImagePromptInput(t0) {
  const $ = _c(17);
  const {
    itemId,
    value,
    original,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== itemId || $[7] !== onSave || $[8] !== original) {
    t4 = () => onSave(itemId, original);
    $[6] = itemId;
    $[7] = onSave;
    $[8] = original;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[10] = onCancel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function ImageHistoryRowComponent(t0) {
  const $ = _c(38);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentItemId,
    editingId,
    editPrompt,
    pendingFocusId,
    onItemClick,
    onEditPromptChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteItem,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    item
  } = row;
  const isEditing = editingId === item.id;
  const isActive = currentItemId === item.id;
  let t1;
  if ($[8] !== item.models) {
    t1 = item.models.every(_temp);
    $[8] = item.models;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const isSaved = t1;
  const firstImage = item.models[0]?.images[0];
  const thumbnailSrc = firstImage ? `data:${firstImage.mediaType};base64,${firstImage.base64}` : null;
  let t2;
  if ($[10] !== editPrompt || $[11] !== isActive || $[12] !== isEditing || $[13] !== item.id || $[14] !== item.prompt || $[15] !== item.timestamp || $[16] !== onCancelEdit || $[17] !== onEditFocused || $[18] !== onEditPromptChange || $[19] !== onItemClick || $[20] !== onSaveEdit || $[21] !== pendingFocusId || $[22] !== thumbnailSrc) {
    t2 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditImagePromptInput itemId={item.id} value={editPrompt} original={item.prompt} shouldFocus={pendingFocusId === item.id} onChange={onEditPromptChange} onSave={onSaveEdit} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton onClick={() => onItemClick(item.id)} isActive={isActive} className="h-full! w-full justify-start group relative pr-2 !transition-none group-hover/image-row:pr-9" type="button"><div className="flex items-start gap-2 min-w-0 w-full">{thumbnailSrc && <img src={thumbnailSrc} alt="Generated image thumbnail" className="h-8 w-8 shrink-0 rounded border object-cover mt-0.5" />}<div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{item.prompt}</div><div className="text-xs text-muted-foreground">{new Date(item.timestamp).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit"
            })}</div></div></div></SidebarMenuButton>;
    $[10] = editPrompt;
    $[11] = isActive;
    $[12] = isEditing;
    $[13] = item.id;
    $[14] = item.prompt;
    $[15] = item.timestamp;
    $[16] = onCancelEdit;
    $[17] = onEditFocused;
    $[18] = onEditPromptChange;
    $[19] = onItemClick;
    $[20] = onSaveEdit;
    $[21] = pendingFocusId;
    $[22] = thumbnailSrc;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== isEditing || $[25] !== isSaved || $[26] !== item.id || $[27] !== item.prompt || $[28] !== onDeleteItem || $[29] !== onStartEdit) {
    t3 = !isEditing && isSaved && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" onClick={_temp2} className="pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/image-row:flex group-hover/image-row:pointer-events-auto group-hover/image-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-40"><DropdownMenuItem onClick={e_0 => {
            e_0.stopPropagation();
            onStartEdit(item.id, item.prompt);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onDeleteItem(item.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[24] = isEditing;
    $[25] = isSaved;
    $[26] = item.id;
    $[27] = item.prompt;
    $[28] = onDeleteItem;
    $[29] = onStartEdit;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== t2 || $[32] !== t3) {
    t4 = <div className="relative h-full px-2 pb-1"><div className="group/image-row relative h-full">{t2}{t3}</div></div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  let t5;
  if ($[34] !== ariaAttributes || $[35] !== style || $[36] !== t4) {
    t5 = <div {...ariaAttributes} style={style}>{t4}</div>;
    $[34] = ariaAttributes;
    $[35] = style;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(m) {
  return !m.isLoading;
}
function groupItemsByDate(items: GalleryItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as GalleryItem[],
    yesterday: [] as GalleryItem[],
    lastWeek: [] as GalleryItem[],
    older: [] as GalleryItem[]
  };
  items.forEach(item => {
    const itemDate = new Date(item.timestamp);
    if (itemDate.toDateString() === today.toDateString()) {
      groups.today.push(item);
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(item);
    } else if (itemDate >= lastWeek) {
      groups.lastWeek.push(item);
    } else {
      groups.older.push(item);
    }
  });
  return groups;
}
export function ImageSidebar(t0) {
  const $ = _c(179);
  const {
    galleryItems,
    isHistoryLoading: t1,
    onNewChat,
    onItemClick,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    currentItemId,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp3);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const listContainerRef = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const orgIdParam = t3;
  let t4;
  if ($[6] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[6] = orgIdParam;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const withOrg = t4;
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t5;
  if ($[8] !== posthog || $[9] !== router || $[10] !== signOut) {
    t5 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://llm-playground.agenzo.com/login");
          }
        }
      });
    };
    $[8] = posthog;
    $[9] = router;
    $[10] = signOut;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const logout = t5;
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t6;
  if ($[12] !== currentTheme || $[13] !== setTheme) {
    t6 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[12] = currentTheme;
    $[13] = setTheme;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const toggleTheme = t6;
  const renameItem = useRenameImageHistory();
  const deleteItem = useDeleteImageHistory();
  const [editingId, setEditingId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [pendingFocusId, setPendingFocusId] = useState(null);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (id, prompt) => {
      setEditingId(id);
      setEditPrompt(prompt);
      setPendingFocusId(id);
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const startEdit = t7;
  let t8;
  if ($[16] !== editPrompt || $[17] !== renameItem) {
    t8 = (id_0, original) => {
      const next = editPrompt.trim();
      if (next && next !== original) {
        renameItem.mutate({
          params: {
            path: {
              id: id_0
            }
          },
          body: {
            prompt: next
          }
        });
      }
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[16] = editPrompt;
    $[17] = renameItem;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const saveEdit = t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const cancelEdit = t9;
  let t10;
  if ($[20] !== deleteItem) {
    t10 = id_1 => {
      deleteItem.mutate({
        params: {
          path: {
            id: id_1
          }
        }
      });
    };
    $[20] = deleteItem;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleDeleteItem = t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setPendingFocusId(null);
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const onEditFocused = t11;
  let t12;
  if ($[23] !== galleryItems) {
    t12 = [...galleryItems].sort(_temp4);
    $[23] = galleryItems;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const sortedItems = t12;
  let t13;
  if ($[25] !== sortedItems) {
    t13 = groupItemsByDate(sortedItems);
    $[25] = sortedItems;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const itemGroups = t13;
  let rows;
  if ($[27] !== itemGroups.lastWeek || $[28] !== itemGroups.older || $[29] !== itemGroups.today || $[30] !== itemGroups.yesterday) {
    const groups = [{
      title: "Today",
      items: itemGroups.today
    }, {
      title: "Yesterday",
      items: itemGroups.yesterday
    }, {
      title: "Last 7 days",
      items: itemGroups.lastWeek
    }, {
      title: "Older",
      items: itemGroups.older
    }];
    rows = [];
    groups.forEach((t14, groupIndex) => {
      const {
        title,
        items: groupedItems
      } = t14;
      if (groupedItems.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedItems.forEach(item => {
        rows.push({
          type: "item",
          key: `item-${item.id}`,
          item
        });
      });
      const hasNextGroupWithItems = groups.slice(groupIndex + 1).some(_temp5);
      if (hasNextGroupWithItems) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[27] = itemGroups.lastWeek;
    $[28] = itemGroups.older;
    $[29] = itemGroups.today;
    $[30] = itemGroups.yesterday;
    $[31] = rows;
  } else {
    rows = $[31];
  }
  const historyRows = rows;
  let t14;
  if ($[32] !== currentItemId || $[33] !== editPrompt || $[34] !== editingId || $[35] !== handleDeleteItem || $[36] !== historyRows || $[37] !== onItemClick || $[38] !== pendingFocusId || $[39] !== saveEdit) {
    t14 = {
      rows: historyRows,
      currentItemId,
      editingId,
      editPrompt,
      pendingFocusId,
      onItemClick,
      onEditPromptChange: setEditPrompt,
      onSaveEdit: saveEdit,
      onCancelEdit: cancelEdit,
      onDeleteItem: handleDeleteItem,
      onStartEdit: startEdit,
      onEditFocused
    };
    $[32] = currentItemId;
    $[33] = editPrompt;
    $[34] = editingId;
    $[35] = handleDeleteItem;
    $[36] = historyRows;
    $[37] = onItemClick;
    $[38] = pendingFocusId;
    $[39] = saveEdit;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const rowProps = t14;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t15;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="size-6" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Image</Badge></Link></div></SidebarHeader>;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    let t16;
    if ($[42] !== className) {
      t16 = <Sidebar className={className}>{t15}</Sidebar>;
      $[42] = className;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    return t16;
  }
  if (!isAuthenticated) {
    let t15;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="size-6" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Image</Badge></Link>;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    let t16;
    let t17;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="font-medium mb-2">Sign in required</div>;
      t17 = <p className="text-muted-foreground mb-3">Please sign in to generate images.</p>;
      $[45] = t16;
      $[46] = t17;
    } else {
      t16 = $[45];
      t17 = $[46];
    }
    let t18;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    let t19;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t15}<div className="w-full rounded-md border p-4 text-sm">{t16}{t17}<div className="flex items-center justify-end gap-2">{t18}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div></SidebarHeader>;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] !== className) {
      t20 = <Sidebar className={className}>{t19}</Sidebar>;
      $[49] = className;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    return t20;
  }
  const t15 = (className ?? "") + " max-md:hidden";
  let t16;
  if ($[51] !== withOrg) {
    t16 = withOrg("/");
    $[51] = withOrg;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  let t18;
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t18 = <span className="text-lg font-bold tracking-tight">LLM Gateway</span>;
    t19 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">Chat</Badge>;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
  } else {
    t17 = $[53];
    t18 = $[54];
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t16) {
    t20 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="LLM Gateway"><Link href={t16} prefetch={true}>{t17}{t18}{t19}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[56] = t16;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  let t22;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Plus className="h-4 w-4" />;
    t22 = <span>New Generation</span>;
    $[58] = t21;
    $[59] = t22;
  } else {
    t21 = $[58];
    t22 = $[59];
  }
  let t23;
  if ($[60] !== onNewChat) {
    t23 = <SidebarMenuItem><SidebarMenuButton onClick={onNewChat} tooltip="New Generation" className="border border-border">{t21}{t22}</SidebarMenuButton></SidebarMenuItem>;
    $[60] = onNewChat;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const t24 = pathname === "/";
  let t25;
  if ($[62] !== withOrg) {
    t25 = withOrg("/");
    $[62] = withOrg;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  let t27;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <MessageSquare className="h-4 w-4" />;
    t27 = <span>Chat</span>;
    $[64] = t26;
    $[65] = t27;
  } else {
    t26 = $[64];
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t25) {
    t28 = <Link href={t25} prefetch={true}>{t26}{t27}</Link>;
    $[66] = t25;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t24 || $[69] !== t28) {
    t29 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Chat" isActive={t24}>{t28}</SidebarMenuButton></SidebarMenuItem>;
    $[68] = t24;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  const t30 = pathname === "/group";
  let t31;
  if ($[71] !== withOrg) {
    t31 = withOrg("/group");
    $[71] = withOrg;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  let t33;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Users className="h-4 w-4" />;
    t33 = <span>Group Chat</span>;
    $[73] = t32;
    $[74] = t33;
  } else {
    t32 = $[73];
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t31) {
    t34 = <Link href={t31} prefetch={true}>{t32}{t33}</Link>;
    $[75] = t31;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t30 || $[78] !== t34) {
    t35 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Group Chat" isActive={t30}>{t34}</SidebarMenuButton></SidebarMenuItem>;
    $[77] = t30;
    $[78] = t34;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  const t36 = pathname === "/image";
  let t37;
  if ($[80] !== withOrg) {
    t37 = withOrg("/image");
    $[80] = withOrg;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  let t39;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ImageIcon className="h-4 w-4" />;
    t39 = <span>Image Studio</span>;
    $[82] = t38;
    $[83] = t39;
  } else {
    t38 = $[82];
    t39 = $[83];
  }
  let t40;
  if ($[84] !== t37) {
    t40 = <Link href={t37} prefetch={true}>{t38}{t39}</Link>;
    $[84] = t37;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== t36 || $[87] !== t40) {
    t41 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Image Studio" isActive={t36}>{t40}</SidebarMenuButton></SidebarMenuItem>;
    $[86] = t36;
    $[87] = t40;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  const t42 = pathname === "/video";
  let t43;
  if ($[89] !== withOrg) {
    t43 = withOrg("/video");
    $[89] = withOrg;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  let t45;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Film className="h-4 w-4" />;
    t45 = <span>Video Studio</span>;
    $[91] = t44;
    $[92] = t45;
  } else {
    t44 = $[91];
    t45 = $[92];
  }
  let t46;
  if ($[93] !== t43) {
    t46 = <Link href={t43} prefetch={true}>{t44}{t45}</Link>;
    $[93] = t43;
    $[94] = t46;
  } else {
    t46 = $[94];
  }
  let t47;
  if ($[95] !== t42 || $[96] !== t46) {
    t47 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Video Studio" isActive={t42}>{t46}</SidebarMenuButton></SidebarMenuItem>;
    $[95] = t42;
    $[96] = t46;
    $[97] = t47;
  } else {
    t47 = $[97];
  }
  const t48 = pathname === "/canvas";
  let t49;
  if ($[98] !== withOrg) {
    t49 = withOrg("/canvas");
    $[98] = withOrg;
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  let t51;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <PenTool className="h-4 w-4" />;
    t51 = <span>Canvas</span>;
    $[100] = t50;
    $[101] = t51;
  } else {
    t50 = $[100];
    t51 = $[101];
  }
  let t52;
  if ($[102] !== t49) {
    t52 = <Link href={t49} prefetch={true}>{t50}{t51}</Link>;
    $[102] = t49;
    $[103] = t52;
  } else {
    t52 = $[103];
  }
  let t53;
  if ($[104] !== t48 || $[105] !== t52) {
    t53 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Canvas" isActive={t48}>{t52}</SidebarMenuButton></SidebarMenuItem>;
    $[104] = t48;
    $[105] = t52;
    $[106] = t53;
  } else {
    t53 = $[106];
  }
  let t54;
  if ($[107] !== t20 || $[108] !== t23 || $[109] !== t29 || $[110] !== t35 || $[111] !== t41 || $[112] !== t47 || $[113] !== t53) {
    t54 = <SidebarHeader><SidebarMenu>{t20}{t23}{t29}{t35}{t41}{t47}{t53}</SidebarMenu></SidebarHeader>;
    $[107] = t20;
    $[108] = t23;
    $[109] = t29;
    $[110] = t35;
    $[111] = t41;
    $[112] = t47;
    $[113] = t53;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[115] = t55;
  } else {
    t55 = $[115];
  }
  let t56;
  if ($[116] !== onSelectOrganization || $[117] !== switcherOrganizations || $[118] !== switcherSelectedOrganization) {
    t56 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[116] = onSelectOrganization;
    $[117] = switcherOrganizations;
    $[118] = switcherSelectedOrganization;
    $[119] = t56;
  } else {
    t56 = $[119];
  }
  let t57;
  if ($[120] !== t56) {
    t57 = <div>{t55}{t56}</div>;
    $[120] = t56;
    $[121] = t57;
  } else {
    t57 = $[121];
  }
  let t58;
  if ($[122] !== galleryItems || $[123] !== historyRows || $[124] !== isHistoryLoading || $[125] !== rowProps) {
    t58 = isHistoryLoading && galleryItems.length === 0 ? <HistorySkeleton withThumbnail={true} /> : galleryItems.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><ImageIcon className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No generation history</p><p className="text-xs text-muted-foreground">Generate an image to see it here</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={ImageHistoryRowComponent} rowCount={historyRows.length} rowHeight={getImageHistoryRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[122] = galleryItems;
    $[123] = historyRows;
    $[124] = isHistoryLoading;
    $[125] = rowProps;
    $[126] = t58;
  } else {
    t58 = $[126];
  }
  let t59;
  if ($[127] !== isHistoryHidden || $[128] !== t58) {
    t59 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t58}</div>;
    $[127] = isHistoryHidden;
    $[128] = t58;
    $[129] = t59;
  } else {
    t59 = $[129];
  }
  let t60;
  if ($[130] !== t57 || $[131] !== t59) {
    t60 = <SidebarContent className="overflow-hidden pb-2">{t57}{t59}</SidebarContent>;
    $[130] = t57;
    $[131] = t59;
    $[132] = t60;
  } else {
    t60 = $[132];
  }
  const t61 = switcherSelectedOrganization ?? organization;
  const t62 = !switcherSelectedOrganization;
  let t63;
  if ($[133] !== isOrgLoading || $[134] !== t61 || $[135] !== t62) {
    t63 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t61} isLoading={isOrgLoading} isChatPlanOrg={t62} /></div>;
    $[133] = isOrgLoading;
    $[134] = t61;
    $[135] = t62;
    $[136] = t63;
  } else {
    t63 = $[136];
  }
  const t64 = user?.name ?? "User";
  let t65;
  if ($[137] !== user?.name) {
    t65 = user?.name?.split(" ").map(_temp6).join("").toUpperCase().slice(0, 2) ?? "U";
    $[137] = user?.name;
    $[138] = t65;
  } else {
    t65 = $[138];
  }
  let t66;
  if ($[139] !== t65) {
    t66 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t65}</span></div>;
    $[139] = t65;
    $[140] = t66;
  } else {
    t66 = $[140];
  }
  const t67 = user?.name;
  let t68;
  if ($[141] !== t67) {
    t68 = <span className="truncate font-semibold">{t67}</span>;
    $[141] = t67;
    $[142] = t68;
  } else {
    t68 = $[142];
  }
  const t69 = user?.email;
  let t70;
  if ($[143] !== t69) {
    t70 = <span className="truncate text-xs text-muted-foreground">{t69}</span>;
    $[143] = t69;
    $[144] = t70;
  } else {
    t70 = $[144];
  }
  let t71;
  if ($[145] !== t68 || $[146] !== t70) {
    t71 = <div className="grid flex-1 text-left text-sm leading-tight">{t68}{t70}</div>;
    $[145] = t68;
    $[146] = t70;
    $[147] = t71;
  } else {
    t71 = $[147];
  }
  let t72;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[148] = t72;
  } else {
    t72 = $[148];
  }
  let t73;
  if ($[149] !== t64 || $[150] !== t66 || $[151] !== t71) {
    t73 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t64} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t66}{t71}{t72}</SidebarMenuButton></DropdownMenuTrigger>;
    $[149] = t64;
    $[150] = t66;
    $[151] = t71;
    $[152] = t73;
  } else {
    t73 = $[152];
  }
  let t74;
  let t75;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llm-gateway.agenzo.com/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t75 = <DropdownMenuSeparator />;
    $[153] = t74;
    $[154] = t75;
  } else {
    t74 = $[153];
    t75 = $[154];
  }
  let t76;
  if ($[155] !== toggleTheme) {
    t76 = event => {
      event.preventDefault();
      toggleTheme();
    };
    $[155] = toggleTheme;
    $[156] = t76;
  } else {
    t76 = $[156];
  }
  let t77;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <span>Theme</span>;
    $[157] = t77;
  } else {
    t77 = $[157];
  }
  let t78;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <div onClick={_temp7} onKeyDown={_temp8}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[158] = t78;
  } else {
    t78 = $[158];
  }
  let t79;
  if ($[159] !== t76) {
    t79 = <DropdownMenuItem className="justify-between gap-3" onSelect={t76}>{t77}{t78}</DropdownMenuItem>;
    $[159] = t76;
    $[160] = t79;
  } else {
    t79 = $[160];
  }
  let t80;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <DropdownMenuSeparator />;
    $[161] = t80;
  } else {
    t80 = $[161];
  }
  let t81;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <LogOut className="mr-2 h-4 w-4" />;
    $[162] = t81;
  } else {
    t81 = $[162];
  }
  let t82;
  if ($[163] !== logout) {
    t82 = <DropdownMenuItem onClick={logout}>{t81}Log out</DropdownMenuItem>;
    $[163] = logout;
    $[164] = t82;
  } else {
    t82 = $[164];
  }
  let t83;
  if ($[165] !== t79 || $[166] !== t82) {
    t83 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t74}{t75}{t79}{t80}{t82}</DropdownMenuContent>;
    $[165] = t79;
    $[166] = t82;
    $[167] = t83;
  } else {
    t83 = $[167];
  }
  let t84;
  if ($[168] !== t73 || $[169] !== t83) {
    t84 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t73}{t83}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[168] = t73;
    $[169] = t83;
    $[170] = t84;
  } else {
    t84 = $[170];
  }
  let t85;
  if ($[171] !== t63 || $[172] !== t84) {
    t85 = <SidebarFooter>{t63}{t84}</SidebarFooter>;
    $[171] = t63;
    $[172] = t84;
    $[173] = t85;
  } else {
    t85 = $[173];
  }
  let t86;
  if ($[174] !== t15 || $[175] !== t54 || $[176] !== t60 || $[177] !== t85) {
    t86 = <Sidebar collapsible="icon" className={t15}>{t54}{t60}{t85}</Sidebar>;
    $[174] = t15;
    $[175] = t54;
    $[176] = t60;
    $[177] = t85;
    $[178] = t86;
  } else {
    t86 = $[178];
  }
  return t86;
}
function _temp8(event_1) {
  return event_1.stopPropagation();
}
function _temp7(event_0) {
  return event_0.stopPropagation();
}
function _temp6(n) {
  return n[0];
}
function _temp5(group) {
  return group.items.length > 0;
}
function _temp4(a, b) {
  return b.timestamp - a.timestamp;
}
function _temp3(org) {
  return !org.isPersonal && !org.isChat;
}